import { useState } from "react";
import type { AuthResponse, AvatarId } from "@howcomet/shared";
import { AVATARS, MAX_GRADE, MIN_GRADE, gradeLabel } from "@howcomet/shared";
import { api, ApiError, getRememberedHandle, rememberHandle } from "../api";

/**
 * Sign-up in one screen, in the order a child can actually handle: who are you,
 * what do you look like, what grade, pick a secret number. The grown-up's part
 * is last and clearly marked as theirs.
 *
 * No email for the child, no password, no "log in with Google" — a seven-year-
 * old has none of those and should not be given a reason to get them.
 */
export function WelcomeScreen({ onSignedIn }: { onSignedIn: (auth: AuthResponse) => void }) {
  const [mode, setMode] = useState<"choose" | "new" | "returning">("choose");

  if (mode === "choose") {
    return (
      <div className="rise mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6">
        <div className="text-center">
          <div className="text-[64px]">☄️</div>
          <h1 className="disp mt-2 text-[40px] leading-none text-cream">Howcomet</h1>
          <p className="mt-3 text-[18px] text-cream/70">
            Ask anything. Find out how come. Collect the stars.
          </p>
        </div>

        <div className="mt-10 flex flex-col gap-3">
          <button
            onClick={() => setMode("new")}
            className="kidbtn disp w-full rounded-3xl bg-gold py-5 text-[22px] text-night shadow-[0_6px_0_rgba(0,0,0,.35)]"
          >
            I'm new here
          </button>
          <button
            onClick={() => setMode("returning")}
            className="kidbtn disp w-full rounded-3xl bg-panel py-5 text-[22px] text-cream"
          >
            I've been here before
          </button>
        </div>
      </div>
    );
  }

  return mode === "new" ? (
    <SignUp onSignedIn={onSignedIn} onBack={() => setMode("choose")} />
  ) : (
    <SignIn onSignedIn={onSignedIn} onBack={() => setMode("choose")} />
  );
}

function SignIn({
  onSignedIn,
  onBack,
}: {
  onSignedIn: (auth: AuthResponse) => void;
  onBack: () => void;
}) {
  const [handle, setHandle] = useState(getRememberedHandle());
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // The pin pad submits on the fourth tap, and at that instant React has not
  // yet re-rendered with the fourth digit — so the completed value is passed in
  // rather than read from state.
  const submit = async (completedPin = pin) => {
    setError("");
    setBusy(true);
    try {
      const auth = await api.login({ handle: handle.trim().toLowerCase(), pin: completedPin });
      rememberHandle(auth.child.handle);
      onSignedIn(auth);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't sign in.");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Frame title="Welcome back" onBack={onBack}>
      <Field label="Your sky name">
        <input
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          placeholder="moon-fox-42"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          className="w-full rounded-2xl bg-panel2 px-5 py-4 text-[20px] text-cream outline-none"
        />
      </Field>

      <Field label="Your secret number">
        <PinInput value={pin} onChange={setPin} onComplete={(complete) => void submit(complete)} />
      </Field>

      {error && <p className="text-[15px] text-amber">{error}</p>}

      <button
        onClick={() => void submit()}
        disabled={handle.trim().length < 3 || pin.length !== 4 || busy}
        className="kidbtn disp mt-2 w-full rounded-3xl bg-gold py-4 text-[21px] text-night shadow-[0_6px_0_rgba(0,0,0,.35)] disabled:bg-cream/15 disabled:text-cream/40 disabled:shadow-none"
      >
        {busy ? "Checking…" : "Let me in"}
      </button>
    </Frame>
  );
}

function SignUp({
  onSignedIn,
  onBack,
}: {
  onSignedIn: (auth: AuthResponse) => void;
  onBack: () => void;
}) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [avatarId, setAvatarId] = useState<AvatarId>("comet");
  const [grade, setGrade] = useState(2);
  const [pin, setPin] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  /** Held between registering and entering, so the handle can be shown once. */
  const [created, setCreated] = useState<AuthResponse | null>(null);

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      const auth = await api.register({
        name: name.trim(),
        avatarId,
        grade,
        pin,
        parentEmail: parentEmail.trim().toLowerCase(),
      });
      rememberHandle(auth.child.handle);
      // Don't drop them straight into the app. The handle is generated by the
      // server and is the only way back in on another device — a child who
      // never sees it has an account they can permanently lose.
      setCreated(auth);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't sign up.");
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return <HandleCard auth={created} onContinue={() => onSignedIn(created)} />;
  }

  const steps = [
    {
      title: "What should I call you?",
      valid: name.trim().length > 0,
      body: (
        <input
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 24))}
          placeholder="Your name"
          autoFocus
          className="w-full rounded-2xl bg-panel2 px-5 py-4 text-[22px] text-cream outline-none"
        />
      ),
    },
    {
      title: "Pick your face",
      valid: true,
      body: (
        <div className="grid grid-cols-4 gap-3">
          {AVATARS.map((avatar) => (
            <button
              key={avatar.id}
              onClick={() => setAvatarId(avatar.id)}
              aria-label={avatar.id}
              aria-pressed={avatarId === avatar.id}
              className={`kidbtn aspect-square rounded-2xl text-[34px] ${
                avatarId === avatar.id ? "bg-gold" : "bg-panel"
              }`}
            >
              {avatar.emoji}
            </button>
          ))}
        </div>
      ),
    },
    {
      title: "What grade are you in?",
      valid: true,
      body: (
        <>
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: MAX_GRADE - MIN_GRADE + 1 }, (_, i) => i + MIN_GRADE).map((g) => (
              <button
                key={g}
                onClick={() => setGrade(g)}
                aria-pressed={grade === g}
                className={`kidbtn disp rounded-2xl py-4 text-[17px] ${
                  grade === g ? "bg-gold text-night" : "bg-panel text-cream"
                }`}
              >
                {g === 0 ? "K" : g}
              </button>
            ))}
          </div>
          {/* No "update my grade" setting exists, because it doesn't need one. */}
          <p className="mt-3 text-[14px] text-cream/50">
            {gradeLabel(grade)} — this moves up on its own every year.
          </p>
        </>
      ),
    },
    {
      title: "Make a secret number",
      valid: pin.length === 4,
      body: (
        <>
          <PinInput value={pin} onChange={setPin} />
          <p className="mt-3 text-[14px] text-cream/50">
            Four numbers you'll remember. You'll type this to get back in.
          </p>
        </>
      ),
    },
    {
      title: "Now a grown-up's turn",
      valid: /.+@.+\..+/.test(parentEmail),
      body: (
        <>
          <p className="mb-4 text-[16px] leading-relaxed text-cream/70">
            What's a grown-up's email? We'll send them their own page, where they set what rewards
            cost and see how you're getting on.
          </p>
          <input
            value={parentEmail}
            onChange={(e) => setParentEmail(e.target.value)}
            placeholder="Grown-up's email"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            className="w-full rounded-2xl bg-panel2 px-5 py-4 text-[18px] text-cream outline-none"
          />
          {/*
            No parent passcode here, deliberately. A passcode typed on this
            tablet, by the child, while the child watches, was never a control —
            and nothing stopped that child inventing an address and becoming
            their own parent. The adult sets their own password from a link we
            email them, on a device this child isn't holding.
          */}
          <p className="mt-4 text-[15px] leading-relaxed text-cream/50">
            They'll get an email to make their own password. It opens on their phone or computer,
            not on this tablet.
          </p>
        </>
      ),
    },
  ];

  const current = steps[step]!;
  const last = step === steps.length - 1;

  return (
    <Frame
      title={current.title}
      onBack={() => (step === 0 ? onBack() : setStep((s) => s - 1))}
      progress={`${step + 1} of ${steps.length}`}
    >
      {current.body}
      {error && <p className="mt-4 text-[15px] text-amber">{error}</p>}

      <button
        onClick={() => (last ? void submit() : setStep((s) => s + 1))}
        disabled={!current.valid || busy}
        className="kidbtn disp mt-6 w-full rounded-3xl bg-gold py-4 text-[21px] text-night shadow-[0_6px_0_rgba(0,0,0,.35)] disabled:bg-cream/15 disabled:text-cream/40 disabled:shadow-none"
      >
        {busy ? "Making your sky…" : last ? "Start collecting stars" : "Next"}
      </button>
    </Frame>
  );
}

/**
 * Shown once, right after sign-up. The handle is the account — lose it and the
 * sky is gone, because there's no email on the child and nothing to reset
 * against. So it gets a screen of its own rather than a line in a toast, and
 * the grown-ups view keeps a copy for when this one is forgotten.
 */
function HandleCard({ auth, onContinue }: { auth: AuthResponse; onContinue: () => void }) {
  const avatar = AVATARS.find((a) => a.id === auth.child.avatarId)?.emoji ?? "⭐";

  return (
    <div className="rise safe-top mx-auto max-w-md px-6 pb-16 pt-10 text-center">
      <div className="text-[60px]">{avatar}</div>
      <h1 className="disp mt-2 text-[30px] leading-tight text-cream">
        Your sky is ready, {auth.child.name}
      </h1>

      <p className="mt-6 text-[17px] text-cream/70">This is your sky name. Keep it somewhere safe.</p>

      <div className="mt-3 rounded-3xl border-2 border-gold bg-panel p-6">
        <p className="disp select-all text-[28px] tracking-wide text-gold">{auth.child.handle}</p>
      </div>

      <p className="mt-4 text-[16px] leading-relaxed text-cream/70">
        You need this and your secret number to get back in on a different tablet. A grown-up can
        always find it again under <b className="text-cream">Grown-ups</b>.
      </p>

      <button
        onClick={onContinue}
        className="kidbtn disp mt-8 w-full rounded-3xl bg-gold py-4 text-[21px] text-night shadow-[0_6px_0_rgba(0,0,0,.35)]"
      >
        Got it — let's go
      </button>
    </div>
  );
}

function Frame({
  title,
  children,
  onBack,
  progress,
}: {
  title: string;
  children: React.ReactNode;
  onBack: () => void;
  progress?: string;
}) {
  return (
    <div className="rise safe-top mx-auto max-w-md px-6 pb-16 pt-8">
      <div className="mb-6 flex items-center justify-between">
        <button onClick={onBack} className="kidbtn text-[16px] text-cream/60">
          ← Back
        </button>
        {progress && <span className="text-[14px] text-cream/40">{progress}</span>}
      </div>
      <h1 className="disp mb-6 text-[30px] leading-tight text-cream">{title}</h1>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <p className="disp mb-2 text-[14px] uppercase tracking-wider text-cream/45">{label}</p>
      {children}
    </div>
  );
}

/** Four big taps, no keyboard. Typing a pin on a tablet keyboard is a barrier. */
function PinInput({
  value,
  onChange,
  onComplete,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Receives the completed pin — state won't hold it yet when this fires. */
  onComplete?: (pin: string) => void;
}) {
  const press = (digit: string) => {
    if (value.length >= 4) return;
    const next = value + digit;
    onChange(next);
    if (next.length === 4) onComplete?.(next);
  };

  return (
    <div>
      <div className="mb-4 flex justify-center gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-4 w-4 rounded-full ${i < value.length ? "bg-gold" : "bg-cream/20"}`}
          />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((digit) => (
          <button
            key={digit}
            onClick={() => press(digit)}
            className="kidbtn disp rounded-2xl bg-panel py-4 text-[24px] text-cream"
          >
            {digit}
          </button>
        ))}
        <button
          onClick={() => onChange("")}
          className="kidbtn rounded-2xl bg-panel/50 py-4 text-[15px] font-bold text-cream/60"
        >
          Clear
        </button>
        <button
          onClick={() => press("0")}
          className="kidbtn disp rounded-2xl bg-panel py-4 text-[24px] text-cream"
        >
          0
        </button>
        <button
          onClick={() => onChange(value.slice(0, -1))}
          className="kidbtn rounded-2xl bg-panel/50 py-4 text-[20px] text-cream/60"
        >
          ⌫
        </button>
      </div>
    </div>
  );
}
